import React, { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { LANGUAGES } from './RecommendationCropDetail';

const NUTRIENT_CODES = ['oc', 'p', 'k', 'n', 'ph', 's', 'ca', 'mg', 'zn', 'b', 'cu', 'fe'];

const defaultGetLevels = (code) => {
  if (code === 'ph') return ['Acidic', 'neutral', 'Alkaline'];
  return ['low', 'medium', 'high'];
};

const emptyI18n = () => Object.fromEntries(LANGUAGES.map((l) => [l, '']));

const CropPrescriptions = ({ prescriptions, constants, states, onUpsert, onDelete, viewMode }) => {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const groupedPrescriptions = (prescriptions || []).reduce((acc, p) => {
    const key = `${p.nutrient_code}:${p.level}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});
  
  const [form, setForm] = useState({
    id: null,
    nutrient_code: 'oc',
    level: 'low',
    input_i18n: emptyI18n(),
    constant_dose: false,
    dose: '',
    per_session_dose: '',
    per_session_dose_is_array: false,
    unit_i18n: emptyI18n(),
    method_i18n: emptyI18n(),
    stage_indices: [1],
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [stageIndicesInput, setStageIndicesInput] = useState('');

  const nutrientCodes = constants?.nutrientCodes || NUTRIENT_CODES;
  const getLevels = constants?.getLevelsForCode || defaultGetLevels;
  const openAdd = (code, level) => {
    setEditing(null);
    const resolvedCode = code || 'oc';
    setForm({
      id: null,
      nutrient_code: resolvedCode,
      level: level || getLevels(resolvedCode)[0],
      input_i18n: emptyI18n(),
      constant_dose: false,
      dose: '',
      per_session_dose: '',
      per_session_dose_is_array: false,
      unit_i18n: emptyI18n(),
      method_i18n: emptyI18n(),
      stage_indices: [1],
      state_id: '',
      min_age: '',
      max_age: '', 
    });
    setStageIndicesInput('1');
    setShowModal(true);
    setMessage('');
  };

  const openEdit = (p) => {
    setEditing(p);
    const indices = Array.isArray(p.stage_indices) ? p.stage_indices : (p.stage_indices ? [p.stage_indices] : [1]);
    setForm({
      id: p.id,
      nutrient_code: p.nutrient_code,
      level: p.level,
      input_i18n: p.input_i18n || emptyI18n(),
      constant_dose: p.constant_dose ?? false,
      dose: p.dose || '',
      per_session_dose: Array.isArray(p.per_session_dose) ? p.per_session_dose : (p.per_session_dose ?? ''),
      per_session_dose_is_array: p.per_session_dose_is_array ?? false,
      unit_i18n: p.unit_i18n || emptyI18n(),
      method_i18n: p.method_i18n || emptyI18n(),
      stage_indices: indices,
      state_id: p.state_id || '',
      min_age: p.min_age ?? '',
      max_age: p.max_age ?? '',
    });
    setStageIndicesInput(indices.join(', '));
    setShowModal(true);
    setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const payload = { ...form };
      const parsedIndices = stageIndicesInput.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n));
      payload.stage_indices = parsedIndices.length ? parsedIndices : [1];
      if (payload.per_session_dose_is_array && typeof payload.per_session_dose === 'string') {
        try {
          payload.per_session_dose = JSON.parse(payload.per_session_dose);
        } catch (_) {
          payload.per_session_dose = payload.per_session_dose.split(',').map((n) => parseFloat(n.trim())).filter((n) => !Number.isNaN(n));
        }
      }
      await onUpsert(form.nutrient_code, form.level, payload);
      setShowModal(false);
    } catch (err) {
      setMessage(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete prescription ${p.nutrient_code} / ${p.level}?`)) return;
    try {
      await onDelete(p.nutrient_code, p.level, p.id); // Passing p.id
    } catch (err) {
      setMessage(err.message || 'Delete failed');
    }
  };

  const setI18n = (field, lang, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: { ...prev[field], [lang]: value },
    }));
  };

  const visibleLangs = LANGUAGES;

  return (
    <div className="card p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Prescriptions (nutrient × level)</h5>
        {!viewMode && (
          <Button variant="primary" size="sm" onClick={() => openAdd()}>
            Add prescription
          </Button>
        )}
      </div>
      {message && <div className="alert alert-warning small">{message}</div>}
      <div className="table-responsive">
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Nutrient</th>
              <th>Level</th>
              <th>Input (EN)</th>
              <th>State</th>
              <th>Age Range</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {nutrientCodes.flatMap((code) => {
              const codeLevels = getLevels(code);
              return codeLevels.flatMap((level) => {
                const list = groupedPrescriptions[`${code}:${level}`] || [];
                
                if (list.length === 0) {
                  return (
                    <tr key={`${code}-${level}-empty`}>
                      <td>{code}</td>
                      <td>{level}</td>
                      <td className="text-muted">—</td>
                      <td>All States</td>
                      <td className="text-muted">All Ages</td>
                      <td>
                        {!viewMode && (
                          <Button variant="outline-primary" size="sm" onClick={() => openAdd(code, level)}>
                            Add
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                }

                return list.map((p, idx) => (
                  <tr key={p.id || `${code}-${level}-${idx}`}>
                    <td>{code}</td>
                    <td>{level}</td>
                    <td>{p?.input_i18n?.english || '—'}</td>
                    <td>{p.state_name || 'All States'}</td>
                    <td>
                      {p.min_age !== '' || p.max_age !== '' 
                        ? `${p.min_age !== '' ? p.min_age : 0} - ${p.max_age !== '' ? p.max_age : '∞'} yrs` 
                        : 'All Ages'}
                    </td>
                    <td>
                      {!viewMode && (
                        <>
                          <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEdit(p)}>
                            Edit
                          </Button>
                          <Button variant="outline-danger" size="sm" className="me-2" onClick={() => handleDelete(p)}>
                            Delete
                          </Button>
                          
                          {idx === list.length - 1 && (
                            <Button variant="outline-secondary" size="sm" onClick={() => openAdd(code, level)}>
                              + Add another
                            </Button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ));
              });
            })}
          </tbody>
        </table>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Edit' : 'Add'} prescription</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label fw-bold">Applicable State</label>
                <select 
                  className="form-select" 
                  value={form.state_id || ''} 
                  onChange={(e) => setForm((f) => ({ ...f, state_id: e.target.value }))}
                >
                  <option value="">All States</option>
                  {states?.map((st) => (
                    <option key={st.id} value={st.id}>
                      {st.name_en}
                    </option>
                  ))}
                </select>
                <small className="text-muted">Leave blank for standard</small>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold">Min Age (Years)</label>
                <input 
                  type="number" 
                  step="0.01"
                  min="0"
                  className="form-control" 
                  placeholder="e.g. 0"
                  value={form.min_age} 
                  onChange={(e) => setForm((f) => ({ ...f, min_age: e.target.value }))} 
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold">Max Age (Years)</label>
                <input 
                  type="number" 
                  step="0.01"
                  min="0"
                  className="form-control" 
                  placeholder="e.g. 5.5"
                  value={form.max_age} 
                  onChange={(e) => setForm((f) => ({ ...f, max_age: e.target.value }))} 
                />
              </div>
            </div>
            <div className="row mb-2">
              <div className="col-md-6">
                <label className="form-label">Nutrient</label>
                <select 
                  className="form-select" 
                  value={form.nutrient_code} 
                  onChange={(e) => {
                    const newCode = e.target.value;
                    const validLevels = getLevels(newCode);
                    setForm((f) => ({ 
                      ...f, 
                      nutrient_code: newCode,
                      level: validLevels.includes(f.level) ? f.level : validLevels[0]
                    }));
                  }} 
                  disabled={!!editing}
                >
                  {nutrientCodes.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Level</label>
                <select 
                  className="form-select" 
                  value={form.level} 
                  onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))} 
                  disabled={!!editing}
                >
                  {getLevels(form.nutrient_code).map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mb-2">
              <label className="form-label fw-bold">Input Parameter</label>
              {visibleLangs.map((lang) => (
                <div key={lang} className="input-group input-group-sm mb-1">
                  <span className="input-group-text text-capitalize" style={{ width: '80px' }}>{lang}</span>
                  <input type="text" className="form-control" placeholder={`Enter in ${lang}...`} value={form.input_i18n[lang] || ''} onChange={(e) => setI18n('input_i18n', lang, e.target.value)} />
                </div>
              ))}
            </div>
            <div className="row mb-2">
              <div className="col-md-4">
                <label className="form-label">Dose</label>
                <input type="text" className="form-control" value={form.dose} onChange={(e) => setForm((f) => ({ ...f, dose: e.target.value }))} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Per session dose</label>
                <input type="text" className="form-control" value={Array.isArray(form.per_session_dose) ? form.per_session_dose.join(', ') : form.per_session_dose} onChange={(e) => setForm((f) => ({ ...f, per_session_dose: e.target.value }))} placeholder="e.g. 150 or 1,2,3" />
              </div>
              <div className="col-md-4 d-flex align-items-end pb-1">
                <div className="form-check">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    id="constantDoseCheck"
                    checked={form.constant_dose} 
                    onChange={(e) => setForm((f) => ({ ...f, constant_dose: e.target.checked }))} 
                  />
                  <label className="form-check-label" htmlFor="constantDoseCheck">
                    Constant Dose
                  </label>
                </div>
              </div>
            </div>
            <div className="mb-2">
              <label className="form-label fw-bold">Unit</label>
              {visibleLangs.map((lang) => (
                <div key={lang} className="input-group input-group-sm mb-1">
                  <span className="input-group-text text-capitalize" style={{ width: '80px' }}>{lang}</span>
                  <input type="text" className="form-control" placeholder={`Enter in ${lang}...`} value={form.unit_i18n[lang] || ''} onChange={(e) => setI18n('unit_i18n', lang, e.target.value)} />
                </div>
              ))}
            </div>
            <div className="mb-2">
              <label className="form-label fw-bold">Method</label>
              {visibleLangs.map((lang) => (
                <div key={lang} className="input-group input-group-sm mb-1">
                  <span className="input-group-text text-capitalize" style={{ width: '80px' }}>{lang}</span>
                  <input type="text" className="form-control" placeholder={`Enter in ${lang}...`} value={form.method_i18n[lang] || ''} onChange={(e) => setI18n('method_i18n', lang, e.target.value)} />
                </div>
              ))}
            </div>
            <div className="mb-2">
              <label className="form-label">Stage indices (comma-separated, 1-based)</label>
              <input type="text" className="form-control" value={stageIndicesInput} onChange={(e) => setStageIndicesInput(e.target.value)} placeholder="1, 2, 4" />
            </div>
            {message && <div className="text-danger small">{message}</div>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default CropPrescriptions;